'use client';

/**
 * Conversation History Section
 *
 * @component_role Explorer section type `conversation_history`
 * @business_rule Displays the list of prior search turns for the current
 *   discovery session. Read-only view of `discoveryStore.history`. Clicking
 *   a turn calls `restoreQuery(turn.query)` which sets a pending restore
 *   flag on the store; DiscoveryComposerSection watches for it and applies
 *   the query to its local input. Empty state shows the moodboard search
 *   pitch when no turns exist.
 *
 *   Labels come from the total-matrix `explorer_labels.discovery` dict
 *   (server-driven, language-aware). No hardcoded strings.
 *
 * @layer presentation
 * @arch_component_type section
 * @arch_domain discovery
 */

import { Clock, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { useDiscoveryStore } from '@/lib/stores/discovery-store';
import type { ExplorerSectionConfig, LabelsDict } from '@/lib/api/types';

export function ConversationHistorySection({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  config,
  labels: L,
}: {
  config: ExplorerSectionConfig;
  labels: LabelsDict;
}) {
  const history = useDiscoveryStore((s) => s.history);
  const loadTurn = useDiscoveryStore((s) => s.loadTurn);

  // Labels not loaded yet
  if (!L.history_empty_heading) return null;

  if (history.length === 0) return null;

  return (
    <div className="border-t border-border">
      <div className="px-3 py-2">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {L.history_section_header}
        </h3>
      </div>
      <div className="py-1">
        {history.map((turn) => (
          <button
            key={turn.id}
            onClick={() => loadTurn(turn)}
            className={cn(
              'w-full flex items-start gap-2 px-3 py-2 text-left',
              'hover:bg-accent/50 transition-colors text-sm',
            )}
          >
            <Clock className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
            <div className="flex-1 min-w-0">
              <p className="truncate">
                {turn.query || `(${turn.imageCount} ${L.history_image_suffix})`}
                {turn.query && turn.imageCount > 0 && (
                  <span className="text-muted-foreground">
                    {' '}
                    + {turn.imageCount} {L.history_image_suffix}
                  </span>
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                {turn.resultCount} {L.history_results_suffix}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
